import LogoutSlidingPanel from "@components/sliders/LogoutSlidingPanel";
import { profileServices } from "@services/apis/Profile";
import { GetProfileData } from "@services/models/Profile/GetProfileModel";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { getUserFeatureConfig } from "src/config/userFeatureConfig";
import LogoImageComponent from "src/modules/authentication/components/LogoImageComponent";
import { setUser } from "../redux/actions";
import { AppState } from "../redux/reducer";
import MobileLayout from "./MobileLayout";
import { buildNavSections } from "./navSections";

const SIDEBAR_WIDTH = "w-[176px]";

const ProfileLoadingShell = () => (
  <div className="flex min-h-screen min-w-screen bg-bg-page-body">
    <aside
      className={`flex ${SIDEBAR_WIDTH} shrink-0 flex-col gap-3 border-r border-border-subtle bg-[#FFFFFF] px-3 py-4`}
    >
      <Skeleton width={88} height={32} className="mb-3 ml-2" />
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} height={36} borderRadius={8} />
      ))}
      <div className="mt-auto">
        <Skeleton height={36} borderRadius={8} />
      </div>
    </aside>
    <main className="flex-1 overflow-hidden">
      <div className="p-4">
        <div className="mb-3 flex gap-3">
          <Skeleton width={320} height={38} borderRadius={8} />
          <Skeleton width={130} height={38} borderRadius={8} />
        </div>
        <div className="overflow-hidden border border-border-subtle">
          <Skeleton height={40} />
          {Array.from({ length: 9 }).map((_, index) => (
            <div
              key={index}
              className="grid grid-cols-6 gap-5 border-t border-border-subtle px-4 py-4"
            >
              {Array.from({ length: 6 }).map((__, cell) => (
                <Skeleton key={cell} height={12} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </main>
  </div>
);

const MobileProfileLoadingShell = () => (
  <div className="flex h-[100dvh] flex-col bg-bg-page-body">
    <header className="flex h-14 items-center justify-between border-b border-border-subtle px-3">
      <Skeleton width={40} height={40} borderRadius={8} />
      <Skeleton width={90} height={32} />
      <Skeleton circle width={40} height={40} />
    </header>
    <div className="space-y-4 p-3">
      <Skeleton width={150} height={16} />
      <Skeleton height={38} borderRadius={8} />
      <Skeleton height={420} borderRadius={8} />
    </div>
  </div>
);

const Layout = ({
  initialSelectedTab,
}: {
  initialSelectedTab?: string;
}) => {
  void initialSelectedTab;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isLogout, setIsLogout] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [errorProfile, setErrorProfile] = useState(false);
  const currentPath = location.pathname || "/";
  const isMobile = useMediaQuery({ query: "(max-width: 640px)" });

  const userDetails = useSelector((state: AppState) => state.user);
  const userFeatureConfig = getUserFeatureConfig(userDetails.userId);
  const sections = buildNavSections(userFeatureConfig);

  const fetchUserData = async () => {
    try {
      setProfileLoading(true);
      setErrorProfile(false);
      const response = await profileServices.getProfile();
      const data: GetProfileData = response.data;
      dispatch(
        setUser({
          userId: data.userId,
          emailId: data.emailId,
          userUUID: data.userUUID,
          profileImage: data.profileImage,
          currentMerchantId: userDetails.currentMerchantId
            ? userDetails.currentMerchantId
            : -1,
          firstName: data.firstName || "",
          merchantDetails: data.merchantDetails,
          personalBalance: data.personalBalance,
          merchantBalances: data.merchantBalances,
          createdAt: data.createdAt,
          intercomUserHash: data.intercomUserHash,
          whatsappNumber: data.whatsappNumber,
          countryCode: data.countryCode,
          p2pAccess: data.p2pAccess,
          kycStatuses: data.kycStatuses,
        }),
      );
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) return;
      setErrorProfile(true);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
    // Profile is loaded once when the workspace shell mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hiddenPaths = [
      !userFeatureConfig.showAnalytics && "/analytics",
      !userFeatureConfig.showDownloadReport && "/download-report",
      !userFeatureConfig.showCPGRates && "/cpg-rates",
      !userFeatureConfig.showCrossRampRates && "/cross-ramp-rates",
    ].filter(Boolean);

    if (hiddenPaths.includes(currentPath)) {
      navigate("/");
    }
  }, [
    currentPath,
    navigate,
    userFeatureConfig.showAnalytics,
    userFeatureConfig.showCPGRates,
    userFeatureConfig.showDownloadReport,
    userFeatureConfig.showCrossRampRates,
  ]);

  if (errorProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-page-body p-6">
        <div className="w-full max-w-sm rounded-xl border border-border-subtle bg-[#FFFFFF] p-6 text-center shadow-sm">
          <span className="material-symbols-outlined mb-3 text-[36px] text-text-status-red">
            error
          </span>
          <h1 className="text-[16px] font-semibold text-text-base">
            Unable to load your workspace
          </h1>
          <p className="mt-2 text-[12px] text-text-subtle">
            We could not retrieve your profile. Please try again.
          </p>
          <button
            type="button"
            className="mt-5 rounded-lg bg-bg-button-primary px-4 py-2 text-[12px] font-semibold text-text-base"
            onClick={() => {
              fetchUserData();
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (profileLoading) {
    return isMobile ? <MobileProfileLoadingShell /> : <ProfileLoadingShell />;
  }

  if (isMobile) {
    return <MobileLayout sections={sections} />;
  }

  return (
    <div className="flex min-h-screen min-w-screen bg-[#F7F8FA]">
      <aside
        className={`sticky top-0 flex h-screen ${SIDEBAR_WIDTH} shrink-0 flex-col border-r border-border-subtle bg-[#FFFFFF] px-3 py-4`}
      >
        <div className="mb-6 px-2">
          <LogoImageComponent className="h-8 w-auto" />
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
          {sections.map((section) => (
            <div key={section.title}>
              <p className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-subtle">
                {section.title}
              </p>
              <div className="flex flex-col gap-0.5">
                {section.items.map((item) => {
                  const active = currentPath === item.path;
                  return (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => navigate(item.path)}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium transition-colors ${
                        active
                          ? "bg-[#E8F6FC] text-[#2436C7]"
                          : "text-[#3A4158] hover:bg-[#F4F6F8]"
                      }`}
                    >
                      <span className="material-symbols-outlined text-[18px] leading-none">
                        {item.icon}
                      </span>
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <button
          type="button"
          onClick={() => setIsLogout(true)}
          className="mt-3 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-[#3A4158] transition-colors hover:bg-[#F4F6F8]"
        >
          <span className="material-symbols-outlined text-[18px] leading-none">
            logout
          </span>
          Logout
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col bg-bg-page-body">
        <div className="flex-grow">
          <LogoutSlidingPanel
            isOpen={isLogout}
            onClose={() => setIsLogout(false)}
            type="send"
            onClickConfirm={() => {
              secureLocalStorage.clear();
              navigate("/login");
            }}
            sliderHeading="Logout"
          />
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
